/**
 * useListingDraft — manages CreateListingPage drafts with backend sync.
 *
 * Behaviour:
 * - On mount (only when NOT editing an existing listing):
 *   1. Load the newest backend draft for the current user (if any).
 *   2. If a backend draft was loaded, mirror it into localStorage and offer
 *      restore via `restoreCandidate`. Otherwise check localStorage for a
 *      legacy local-only draft (offline fallback).
 *
 * - Every 30 s, if the form has any meaningful content (title / brand /
 *   model / image), persist to BOTH localStorage (fast & resilient) and
 *   the backend (cross-device). Backend errors are silent so the user
 *   keeps editing offline.
 *
 * - `saveNow()`, `restore()`, `discard()` and `clear()` mirror the previous
 *   localStorage-only contract so the consuming component stays small.
 *
 * Persisted draft id is kept inside the same localStorage entry to allow
 * idempotent upserts on the backend (PUT-like semantics).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";

const DRAFT_TTL_HOURS = 24;
const STORAGE_PREFIX = "tw_create_listing_draft_v1_";

function storageKey(userId) {
    return `${STORAGE_PREFIX}${userId || "anon"}`;
}

function readLocal(userId) {
    try {
        const raw = localStorage.getItem(storageKey(userId));
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed?.savedAt || !parsed?.form) return null;
        const savedAt = new Date(parsed.savedAt);
        const ageHours = (Date.now() - savedAt.getTime()) / 36e5;
        if (ageHours > DRAFT_TTL_HOURS) {
            localStorage.removeItem(storageKey(userId));
            return null;
        }
        return { id: parsed.id || null, savedAt, form: parsed.form };
    } catch {
        try { localStorage.removeItem(storageKey(userId)); } catch { /* noop */ }
        return null;
    }
}

function writeLocal(userId, payload) {
    try {
        localStorage.setItem(storageKey(userId), JSON.stringify(payload));
    } catch {
        /* quota or disabled — ignore */
    }
}

function clearLocal(userId) {
    try { localStorage.removeItem(storageKey(userId)); } catch { /* noop */ }
}

function hasMeaningfulContent(form) {
    if (!form) return false;
    return Boolean(
        form.title || form.brand || form.model
        || (Array.isArray(form.images) && form.images.length > 0),
    );
}

export default function useListingDraft({ user, isEdit, form }) {
    const userId = user?.id;
    const [savedAt, setSavedAt] = useState(null);
    const [restoreCandidate, setRestoreCandidate] = useState(null);
    const draftIdRef = useRef(null);

    // 1) On mount: try to load backend draft, fall back to localStorage.
    useEffect(() => {
        if (isEdit || !user) return;
        let cancelled = false;
        (async () => {
            // Backend first
            try {
                const res = await api.get("/listing-drafts");
                const items = res?.data?.items || [];
                if (!cancelled && items.length > 0) {
                    const top = items[0];
                    if (hasMeaningfulContent(top.form)) {
                        const at = new Date(top.updated_at || top.created_at);
                        draftIdRef.current = top.id;
                        writeLocal(userId, {
                            id: top.id,
                            savedAt: at.toISOString(),
                            form: top.form,
                        });
                        setRestoreCandidate({ form: top.form, savedAt: at });
                        return;
                    }
                }
            } catch {
                /* offline / anon — silently fall back to localStorage */
            }
            // localStorage fallback (offline-mode users)
            const local = readLocal(userId);
            if (!cancelled && local && hasMeaningfulContent(local.form)) {
                draftIdRef.current = local.id;
                setRestoreCandidate({ form: local.form, savedAt: local.savedAt });
            }
        })();
        return () => { cancelled = true; };
    }, [user, isEdit, userId]);

    // 2) Auto-save every 30 s when there's something worth saving.
    useEffect(() => {
        if (isEdit || !user) return undefined;
        const timer = setInterval(() => {
            if (!hasMeaningfulContent(form)) return;
            const ts = new Date();
            writeLocal(userId, {
                id: draftIdRef.current,
                savedAt: ts.toISOString(),
                form,
            });
            setSavedAt(ts);
            // Backend sync (silent on failure)
            api.post("/listing-drafts", { id: draftIdRef.current, form })
                .then((res) => {
                    const d = res?.data;
                    if (d?.id && d.id !== draftIdRef.current) {
                        draftIdRef.current = d.id;
                        writeLocal(userId, { id: d.id, savedAt: ts.toISOString(), form });
                    }
                })
                .catch(() => { /* offline — localStorage still up-to-date */ });
        }, 30000);
        return () => clearInterval(timer);
    }, [form, user, isEdit, userId]);

    const saveNow = useCallback(async () => {
        if (!hasMeaningfulContent(form)) {
            toast.error("Bitte zuerst ein paar Felder ausfüllen, bevor du speichern kannst.");
            return;
        }
        const ts = new Date();
        writeLocal(userId, { id: draftIdRef.current, savedAt: ts.toISOString(), form });
        setSavedAt(ts);
        try {
            const res = await api.post("/listing-drafts", { id: draftIdRef.current, form });
            if (res?.data?.id) {
                draftIdRef.current = res.data.id;
                writeLocal(userId, { id: res.data.id, savedAt: ts.toISOString(), form });
            }
            toast.success("Entwurf gespeichert — du kannst jederzeit zurückkehren.");
        } catch (_e) {
            // localStorage is already up-to-date.
            toast.success("Entwurf lokal gespeichert (offline).");
        }
    }, [form, userId]);

    const restore = useCallback((applyForm) => {
        if (!restoreCandidate) return;
        applyForm(restoreCandidate.form);
        setRestoreCandidate(null);
        toast.success("Entwurf wiederhergestellt.");
    }, [restoreCandidate]);

    const discard = useCallback(async () => {
        clearLocal(userId);
        setRestoreCandidate(null);
        const id = draftIdRef.current;
        draftIdRef.current = null;
        if (id) {
            try { await api.delete(`/listing-drafts/${id}`); } catch { /* noop */ }
        }
    }, [userId]);

    const clearAfterPublish = useCallback(async () => {
        clearLocal(userId);
        const id = draftIdRef.current;
        draftIdRef.current = null;
        if (id) {
            try { await api.delete(`/listing-drafts/${id}`); } catch { /* noop */ }
        }
    }, [userId]);

    return {
        savedAt,
        restoreCandidate,
        saveNow,
        restore,
        discard,
        clearAfterPublish,
    };
}
